import Fuse from "fuse.js";

import { logger } from "@/src/lib/logging/logger";
import { FuzzyNameMatchThreshold, isExactNameMatch, matchByName } from "@/src/lib/name-match";
import {
  matchSpokenNameWithPhoneAndDob,
  type PatientMatchingOptions,
} from "@/src/lib/patient-matching/spoken-name-match";
import { normalizeAddressForComparison } from "@/src/lib/utils/address-normalization";
import { hasValue } from "@/src/lib/utils/object-utils";
import { normalizeForComparison, FieldType } from "@/src/lib/utils/string-utils";
import { Location } from "@/src/models/location";
import { Patient } from "@/src/models/patient";
import { Practitioner } from "@/src/models/practitioner";

/**
 * Checks if two patients have conflicting identifying field values.
 * Only checks fields where both patients have non-empty values to avoid false negatives.
 * Phone numbers are normalized (digits only) and emails are lowercased before comparison.
 *
 * @param patient1 - First patient to compare
 * @param patient2 - Second patient to compare
 * @returns `true` if conflicts are found, `false` otherwise
 */
export function hasConflictingIdentifyingFields(patient1: Partial<Patient>, patient2: Partial<Patient>): boolean {
  // Fields that are highly identifying and should match if both are present
  const identifyingFields: Array<{ key: keyof Patient; type: FieldType }> = [
    { key: "ssn", type: "text" },
    { key: "mobilePhoneNumber", type: "tel" },
    { key: "homePhoneNumber", type: "tel" },
    { key: "personalEmail", type: "text" },
    { key: "ehrId", type: "text" },
  ];

  return identifyingFields.some(({ key, type }) => {
    const value1 = patient1[key];
    const value2 = patient2[key];

    // Only check for conflicts if BOTH patients have a non-empty value
    if (!hasValue(value1) || !hasValue(value2)) {
      return false;
    }

    const normalized1 = normalizeForComparison(value1, type);
    const normalized2 = normalizeForComparison(value2, type);

    // If both have valid normalized values (not empty, not "N/A") and they don't match, this is a conflict
    return normalized1 && normalized2 && normalized1 !== "N/A" && normalized2 !== "N/A" && normalized1 !== normalized2;
  });
}

/**
 * Filter external patient search results requiring exact matches on first name, last name, and date of birth.
 * Filters out any matches with conflicting identifying fields (SSN, phone, email, ehrId).
 *
 * @param targetPatient - The target patient to match against (must have givenName, familyName, dateOfBirth)
 * @param searchResults - Array of patients to filter
 * @returns Filtered array of matching patients (excluding those with conflicts)
 */
export function filterPatientSearchResults(targetPatient: Patient, searchResults: Patient[]): Patient[] {
  const { givenName, familyName, dateOfBirth } = targetPatient;

  if (!givenName || !familyName || !dateOfBirth) {
    return [];
  }

  // Filter for exact matches on first name, last name, and date of birth
  const exactMatches = searchResults.filter((result) => nameAndDobMatch(targetPatient, result));

  // If there is only one exact match, return it
  if (exactMatches.length === 1) {
    return exactMatches;
  }

  // Filter out matches with conflicts in additional identifying fields
  return exactMatches.filter((candidatePatient) => !hasConflictingIdentifyingFields(targetPatient, candidatePatient));
}

export function filterPatientsByDob(targetPatient: Pick<Patient, "dateOfBirth">, searchResults: Patient[]): Patient[] {
  const targetDob = targetPatient.dateOfBirth;

  if (!targetDob) {
    return [];
  }

  return searchResults.filter((searchResult) => searchResult.dateOfBirth === targetDob);
}

type PhoneCarrier = Pick<Patient, "mobilePhoneNumber" | "homePhoneNumber" | "workPhoneNumber">;

/** Normalized (digits-only, NANP) phone numbers a patient record carries, ignoring blanks. */
function patientPhoneDigits(patient: PhoneCarrier): string[] {
  return [patient.mobilePhoneNumber, patient.homePhoneNumber, patient.workPhoneNumber]
    .map((phone) => normalizeForComparison(phone, "tel"))
    .filter((digits) => digits !== "" && digits !== "N/A");
}

export function matchByPhoneThenName(
  candidates: Patient[],
  query: Patient,
  options: PatientMatchingOptions = {}
): Patient[] {
  if (candidates.length === 0) {
    return [];
  }

  // Voice never selects a chart on phone + DOB alone: the name must be exact or explained by a name check.
  if (options.matchSpokenNameVariants) {
    const dobCandidates = filterPatientsByDob(query, candidates);
    const exactMatches = dobCandidates.filter((candidate) => isExactNameMatch(candidate, query));

    if (exactMatches.length > 1) {
      const queryPhones = patientPhoneDigits(query);
      const phoneExactMatches = exactMatches.filter((candidate) =>
        patientPhoneDigits(candidate).some((digits) => queryPhones.includes(digits))
      );

      return phoneExactMatches.length > 0 ? phoneExactMatches : exactMatches;
    }

    if (exactMatches.length === 1) return exactMatches;

    const corroboratedMatches = dobCandidates.filter((candidate) => matchSpokenNameWithPhoneAndDob(candidate, query));

    // Preserve all qualifying records so the caller can surface ambiguity instead of choosing a winner.
    if (corroboratedMatches.length > 0) return corroboratedMatches;

    // An EHR search is not a complete same-DOB set, so without a shared phone only misentries qualify.
    return matchByName(dobCandidates, query);
  }

  const queryPhones = patientPhoneDigits(query);

  if (queryPhones.length > 0) {
    const phoneMatches = candidates.filter((candidate) =>
      patientPhoneDigits(candidate).some((digits) => queryPhones.includes(digits))
    );

    if (phoneMatches.length > 0) {
      logger.info("matchByPhoneThenName: phone match found", {
        phoneMatchCount: phoneMatches.length,
        candidateCount: candidates.length,
        matchedIds: phoneMatches.map((p) => p.id),
      });
    }

    if (phoneMatches.length === 1) {
      return phoneMatches;
    }

    if (phoneMatches.length > 1) {
      const narrowed = matchByName(phoneMatches, query, FuzzyNameMatchThreshold.Loose);

      return narrowed.length > 0 ? narrowed : phoneMatches;
    }
  }

  return matchByName(candidates, query, FuzzyNameMatchThreshold.Strict, { mergeTiers: queryPhones.length === 0 });
}

export function matchPatientSearchResults(
  query: Patient,
  results: Patient[],
  options: PatientMatchingOptions = {}
): Patient[] {
  return matchByPhoneThenName(filterPatientsByDob(query, results), query, options);
}

// Matches common name suffixes (Jr., Sr., II, III, etc.) that some EHRs (e.g. ModMed) embed
// inside the family-name field rather than storing them in a dedicated suffix field.
const NAME_SUFFIX_PATTERN = /[\s,]+(jr\.?|sr\.?|junior|senior|ii|iii|iv|v|vi|vii|viii|2nd|3rd|4th|5th)\.?$/i;

export function stripNameSuffix(name: string | null | undefined): string {
  if (!name) return "";

  return name.replace(NAME_SUFFIX_PATTERN, "").trim();
}

export function nameAndDobMatch(targetPatient: Patient, searchResult: Patient): boolean {
  const targetDob = targetPatient.dateOfBirth;
  const resultDob = searchResult.dateOfBirth;

  if (!targetDob || !resultDob || targetDob !== resultDob) {
    return false;
  }

  const resultGivenName = searchResult.givenName?.toLowerCase().trim();
  const resultFamilyName = searchResult.familyName?.toLowerCase().trim();
  const targetGivenName = targetPatient.givenName?.toLowerCase().trim();
  const targetFamilyName = targetPatient.familyName?.toLowerCase().trim();

  return resultGivenName === targetGivenName && stripNameSuffix(resultFamilyName) === stripNameSuffix(targetFamilyName);
}

export function dobMatch(targetPatient: Patient, searchResult: Patient): boolean {
  const targetDob = targetPatient.dateOfBirth;
  const resultDob = searchResult.dateOfBirth;

  if (!targetDob || !resultDob || targetDob !== resultDob) {
    return false;
  }

  return true;
}

// Function to normalize NPI number
export function normalizeNpi(npi: string | null): string | null {
  if (!npi) return null;
  const cleanNpi = npi.replace(/\D/g, "");

  return /^\d{10}$/.test(cleanNpi) ? cleanNpi : null;
}

export interface NameSimilarityOptions {
  allowLengthMismatch?: boolean;
}

export function nameSimilarity(name1: string | null, name2: string | null, options?: NameSimilarityOptions): number {
  if (!name1 || !name2) return 0;

  const name1Lower = name1.toLowerCase().trim();
  const name2Lower = name2.toLowerCase().trim();

  if (name1Lower === name2Lower) return 1;

  if (!options?.allowLengthMismatch && Math.abs(name1Lower.length - name2Lower.length) > 1) {
    return 0;
  }

  // Always treat the longer string as the document and the shorter as the query
  const [doc, query] = name1Lower.length >= name2Lower.length ? [name1Lower, name2Lower] : [name2Lower, name1Lower];

  const fuse = new Fuse([doc], {
    includeScore: true,
    threshold: 0.3,
    location: 0,
    distance: 100,
    minMatchCharLength: 1,
  });

  const result = fuse.search(query);

  if (result.length === 0) return 0;

  // Convert Fuse.js score to similarity (0-1)
  const fuseScore = result[0].score || 1;

  return 1 - fuseScore;
}

// Function to calculate overall similarity between two practitioners
export function calculatePractitionerSimilarity(p1: Practitioner, p2: Practitioner): number {
  let score = 0;
  let totalWeights = 0;

  // NPI number match (highest weight) - must be exact 10-digit match
  const p1Npi = normalizeNpi(p1.npiNumber);
  const p2Npi = normalizeNpi(p2.npiNumber);

  if (p1Npi && p2Npi && p1Npi === p2Npi) {
    score += 1 * 0.5;

    totalWeights += 0.5;
  }

  // Name similarity using Fuse.js
  const givenNameSim = nameSimilarity(p1.givenName, p2.givenName);
  const familyNameSim = nameSimilarity(p1.familyName, p2.familyName);

  // Require at least 0.8 similarity for given names to consider it a match
  if (givenNameSim < 0.8) return 0;

  if (familyNameSim < 0.8) return 0;

  score += givenNameSim * 0.3;

  score += familyNameSim * 0.1;

  totalWeights += 0.4;

  // Specialty match using Fuse.js
  if (p1.specialty && p2.specialty) {
    const specialtyFuse = new Fuse([p1.specialty], {
      includeScore: true,
      threshold: 0.3,
      location: 0,
      distance: 100,
      minMatchCharLength: 1,
    });

    const specialtyResult = specialtyFuse.search(p2.specialty);

    if (specialtyResult.length > 0) {
      const specialtyScore = 1 - (specialtyResult[0].score || 1);

      score += specialtyScore * 0.1;

      totalWeights += 0.1;
    }
  }

  return totalWeights > 0 ? score / totalWeights : 0;
}

// Function to score a practitioner's data completeness
export function getPractitionerCompletenessScore(p: Practitioner): number {
  let score = 0;

  // NPI number (highest weight) - must be valid
  const normalizedNpi = normalizeNpi(p.npiNumber);

  if (normalizedNpi) score += 3;

  // Name components
  if (p.givenName) score += 1;

  if (p.familyName) score += 1;

  if (p.middleName) score += 0.5;

  // Specialty
  if (p.specialty) score += 1;

  return score;
}

// Function to select the canonical practitioner from a group
export function selectCanonicalPractitioner(group: Practitioner[]): Practitioner {
  return group.reduce((best, current) => {
    const bestScore = getPractitionerCompletenessScore(best);
    const currentScore = getPractitionerCompletenessScore(current);

    return currentScore > bestScore ? current : best;
  }, group[0]);
}

export function calculateLocationSimilarity(l1: Location, l2: Location): number {
  let score = 0;
  let totalWeights = 0;

  // Address match (highest weight) - case insensitive
  if (l1.address && l2.address) {
    const address1 =
      `${l1.address.line1 || ""} ${l1.address.line2 || ""} ${l1.address.city || ""} ${l1.address.state || ""} ${l1.address.postalCode || ""}`.trim();
    const address2 =
      `${l2.address.line1 || ""} ${l2.address.line2 || ""} ${l2.address.city || ""} ${l2.address.state || ""} ${l2.address.postalCode || ""}`.trim();
    const addressSim = nameSimilarity(normalizeAddressForComparison(address1), normalizeAddressForComparison(address2));

    score += addressSim * 0.4;

    totalWeights += 0.4;
  }

  // Phone number match
  if (l1.phoneNumber && l2.phoneNumber) {
    const phoneSim = nameSimilarity(l1.phoneNumber, l2.phoneNumber);

    score += phoneSim * 0.3;

    totalWeights += 0.3;
  }

  // Fax number match
  if (l1.faxPhoneNumber && l2.faxPhoneNumber) {
    const faxSim = nameSimilarity(l1.faxPhoneNumber, l2.faxPhoneNumber);

    score += faxSim * 0.15;

    totalWeights += 0.15;
  }

  // Name match (lowest weight)
  if (l1.name && l2.name) {
    const nameSim = nameSimilarity(l1.name, l2.name);

    score += nameSim * 0.05;

    totalWeights += 0.05;
  }

  return totalWeights > 0 ? score / totalWeights : 0;
}

function getLocationCompletenessScore(l: Location): number {
  let score = 0;

  if (l.name) score += 1;

  if (l.address) score += 1;

  if (l.phoneNumber) score += 1;

  return score;
}

export function selectCanonicalLocation(group: Location[]): Location {
  return group.reduce((best, current) => {
    const bestScore = getLocationCompletenessScore(best);
    const currentScore = getLocationCompletenessScore(current);

    return currentScore > bestScore ? current : best;
  }, group[0]);
}
